/* eslint-disable @typescript-eslint/indent */
import React, { createContext, useState, useContext, useRef, useEffect, useMemo } from 'react';
import { SearchState } from '@customTypes/search';
import { parse } from 'date-fns';

// AcceptedParameters is a utility type that takes a type K
// and returns an object with the same keys as K,
// but with boolean values. This is useful for updating the search parameters.
type AcceptedParameters<T> = { [k in keyof T]?: boolean };

// TODO: Maybe these NonContext exports should be moved to a different file
export const defaultSearchParams: SearchState = {
  q: '',
  date: '',
  product_category: '',
  city: '',
  search_term: '',
  state_code: '',
  country_code: '',
  hotel_name: '',
  filters: {
    amenities: {
      beach: false,
      'all-inclusive': false,
      'free-parking': false,
      'luggage-storage': false,
      'airport-shuttle': false,
      'cruise-shuttle': false,
      'fitness-center': false,
      wheelchair: false,
      showers: false,
      lockers: false,
      'outdoor-pool': false,
      'indoor-pool': false,
      'infinity-pool': false,
      'kiddie-pool': false,
      'rooftop-pool': false,
      hottub: false,
      waterslide: false,
      lazyriver: false,
      waterpark: false,
      'splash-pad': false,
      'relaxation-lounge': false,
      sauna: false,
      'infrared-sauna': false,
      'steam-room': false,
      'salt-room': false,
      'thermal-pool': false,
      'cold-plunge': false,
      'led-and-red-light-therapy': false,
    },
    hotel_stars: {
      '=5': false,
      '=4': false,
      '=3': false,
    },
    guest_ratings: {
      '>=3.8': false,
    },
    vibes: {
      'family-friendly': false,
      party: false,
      serene: false,
      luxe: false,
      trendy: false,
    },
    only_available: false,
    only_recent: false,
    product_types: [],
    city_alias_id: 0,
  },
};

export function GetDefaultSearchState(): SearchState {
  return JSON.parse(JSON.stringify(defaultSearchParams)) as SearchState;
}

export const HotelStarsFriendlyNames: {
  [key in keyof SearchState['filters']['hotel_stars']]: string;
} = {
  '=5': '5-star',
  '=4': '4-star',
  '=3': '3-star',
};

export const GuestRatingFriendlyNames: {
  [key in keyof SearchState['filters']['guest_ratings']]: string;
} = {
  '>=3.8': 'Top Rated',
};

export const VibesFriendlyNames: { [key in keyof SearchState['filters']['vibes']]: string } = {
  'family-friendly': 'Family Friendly',
  party: 'Party',
  serene: 'Serene',
  luxe: 'Luxe',
  trendy: 'Trendy',
};

export const AmenitiesFriendlyNames: {
  [key in keyof SearchState['filters']['amenities']]: string;
} = {
  beach: 'Beach Access',
  'all-inclusive': 'All-Inclusive',
  'free-parking': 'Free Parking',
  'luggage-storage': 'Luggage Storage',
  'airport-shuttle': 'Airport Shuttle',
  'cruise-shuttle': 'Cruise Port Shuttle',
  'fitness-center': 'Gym',
  wheelchair: 'Wheelchair Accessible',
  showers: 'Showers',
  lockers: 'Lockers',
  'outdoor-pool': 'Outdoor Pool',
  'indoor-pool': 'Indoor Pool',
  'infinity-pool': 'Infinity Pool',
  'kiddie-pool': 'Kiddie Pool',
  'rooftop-pool': 'Rooftop Pool',
  hottub: 'Hot Tub',
  waterslide: 'Waterslide',
  lazyriver: 'Lazy River',
  waterpark: 'Waterpark',
  'splash-pad': 'Splash Pad',
  'relaxation-lounge': 'Relaxation Lounge',
  sauna: 'Sauna',
  'infrared-sauna': 'Infrared Sauna',
  'steam-room': 'Steam Room',
  'salt-room': 'Salt Room',
  'thermal-pool': 'Thermal Pool',
  'cold-plunge': 'Cold Plunge',
  'led-and-red-light-therapy': 'LED/Red Light Therapy',
};

export const PoolFeatures: Array<keyof SearchState['filters']['amenities']> = [
  'hottub',
  'outdoor-pool',
  'rooftop-pool',
  'infinity-pool',
  'indoor-pool',
  'kiddie-pool',
  'waterpark',
  'splash-pad',
  'lazyriver',
  'waterslide',
];

export const PoolProductTypes = {
  Cabana: 1,
  Daybed: 4,
};

export const SpaFeatures: Array<keyof SearchState['filters']['amenities']> = [
  'sauna',
  'steam-room',
  'cold-plunge',
  'infrared-sauna',
  'salt-room',
  'thermal-pool',
  'relaxation-lounge',
  'led-and-red-light-therapy',
];

export const SpaProductTypes = {
  'Spa Pass': 3,
  Massage: 7,
  'Body Treatment': 50,
  Couples: 53,
  Facial: 51,
  Nails: 52,
};

export const ProductCategorySpecificFilters: Set<keyof SearchState['filters']['amenities']> =
  new Set([...PoolFeatures, ...SpaFeatures]);

export type SearchContextType = {
  searchParams: SearchState;
  getDate: () => Date | 'any' | '';
  setAmenities: (
    amenities: AcceptedParameters<SearchState['filters']['amenities']>,
  ) => SearchContextType;
  setHotelStars: (
    hotelStars: AcceptedParameters<SearchState['filters']['hotel_stars']>,
  ) => SearchContextType;
  setGuestRatings: (
    guestRatings: AcceptedParameters<SearchState['filters']['guest_ratings']>,
  ) => SearchContextType;
  setVibes: (vibes: AcceptedParameters<SearchState['filters']['vibes']>) => SearchContextType;
  setOnlyAvailable: (onlyAvailable: boolean) => SearchContextType;
  setCity: (city: string) => SearchContextType;
  setHotelName: (hotelName: string) => SearchContextType;
  setSearchTerm: (term: string) => SearchContextType;
  setStateCode: (stateCode: string) => SearchContextType;
  setCountryCode: (countryCode: string) => SearchContextType;
  setProductCategory: (productCategory: string) => SearchContextType;
  setDate: (date: Date | 'any' | '') => SearchContextType;
  setOnlyRecent: (onlyRecent: boolean) => SearchContextType;
  addProductType: (productType: number) => SearchContextType;
  removeProductType: (productType: number) => SearchContextType;
  updateSearchState: (state: SearchState) => SearchContextType;
  search: (debounce?: number) => void;
  clearFilters: () => SearchContextType;
  setAliasId: (aliasId: number) => SearchContextType;
  status: 'idle' | 'searchStart' | 'searchEnd';
};

export function GetAmenitiesToShow(
  productCategory: string,
): Array<keyof SearchState['filters']['amenities']> {
  // https://docs.google.com/document/d/1W4gPkLtkh2yWSTrMQiKraXJQpwaXaPFV2l4Tii8Cc6g/edit?tab=t.0#heading=h.lj31hk6jbexf
  //
  // Per the PRD here, Spa and Day Room categories should hide certain amenities.
  const excludeAmenities: Set<keyof SearchState['filters']['amenities']> = (() => {
    let keys: Array<keyof SearchState['filters']['amenities']> = [];
    const normalizedCategory = productCategory.toLowerCase();
    if (normalizedCategory === 'spa') {
      keys = ['all-inclusive', 'beach', 'wheelchair', 'showers', 'lockers'];
    }

    if (normalizedCategory === 'day room') {
      keys = ['showers', 'lockers'];
    }
    return new Set(keys);
  })();

  return (
    Object.keys(defaultSearchParams.filters.amenities) as Array<
      keyof SearchState['filters']['amenities']
    >
  ).filter((key) => !excludeAmenities.has(key) && !ProductCategorySpecificFilters.has(key));
}

/**
 * Get the number of applied filters
 */
export function GetAppliedFiltersCount(s: SearchState): number {
  const { filters } = s;
  const extractApplied = (k: { [key: string]: boolean }) => Object.values(k).filter((v) => v);

  const appliedSRPFilters: Array<boolean> = [
    ...(filters.only_available ? [true] : []),
    ...extractApplied(filters.hotel_stars),
    ...extractApplied(filters.guest_ratings),
    ...extractApplied(filters.vibes),
    ...extractApplied(filters.amenities),
    ...filters.product_types.map(() => true),
  ];
  return appliedSRPFilters.length;
}

/**
 * GetSearchStateOnCategoryChange resets search filters
 * depending on the category change.
 *
 * @param oldSearchState - The previous search state
 * @param newSearchState - The new search state
 * @return The new search state with updated filters
 */
export function GetSearchStateOnCategoryChange(
  oldSearchState: SearchState,
  newSearchState: SearchState,
): SearchState {
  const oldCategory = oldSearchState.product_category.toLowerCase();
  const newCategory = newSearchState.product_category.toLowerCase();
  const copy = { ...newSearchState };

  if (oldCategory !== newCategory) {
    // Reset amenities and product_types if category changed
    copy.filters.amenities = { ...defaultSearchParams.filters.amenities };
    copy.filters.product_types = [];
    // Reset vibes only for spa or day room
    if (['spa', 'day room'].includes(newCategory)) {
      copy.filters.vibes = { ...defaultSearchParams.filters.vibes };
    }
  }
  return copy;
}

export function useDefaultContextImplementation(
  s?: SearchState,
  customSearch?: (s: SearchState) => Promise<void>,
): SearchContextType {
  const [status, setStatus] = useState<'idle' | 'searchStart' | 'searchEnd'>('idle');
  const [searchParams, setSearchParams] = useState<SearchState>(
    JSON.parse(JSON.stringify(s ?? defaultSearchParams)) as SearchState,
  );
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customSearcherRef = useRef(customSearch);
  useEffect(() => {
    customSearcherRef.current = customSearch;
  }, [customSearch]);

  useEffect(() => {
    if (status === 'searchEnd') {
      setStatus('idle');
    }
  }, [status]);

  return useMemo(
    () => ({
      status,
      searchParams,
      getDate() {
        if (searchParams.date === '') return '';
        if (searchParams.date === 'any') return 'any';
        return parse(searchParams.date, 'yyyy-MM-dd', new Date());
      },
      setAmenities(amenities: AcceptedParameters<SearchState['filters']['amenities']>) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.amenities = { ...p.filters.amenities, ...amenities };
          return copy;
        });
        return this;
      },
      setHotelStars(hotelStars: AcceptedParameters<SearchState['filters']['hotel_stars']>) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.hotel_stars = {
            ...p.filters.hotel_stars,
            ...hotelStars,
          };
          return copy;
        });

        return this;
      },
      setGuestRatings(guestRatings: AcceptedParameters<SearchState['filters']['guest_ratings']>) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.guest_ratings = {
            ...p.filters.guest_ratings,
            ...guestRatings,
          };
          return copy;
        });
        return this;
      },
      setVibes(vibes: AcceptedParameters<SearchState['filters']['vibes']>) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.vibes = {
            ...p.filters.vibes,
            ...vibes,
          };
          return copy;
        });
        return this;
      },
      setOnlyAvailable(onlyAvailable: boolean) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.only_available = onlyAvailable;
          return copy;
        });
        return this;
      },
      setOnlyRecent(onlyRecent: boolean) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.only_recent = onlyRecent;
          return copy;
        });
        return this;
      },
      setHotelName(hotelName: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.hotel_name = hotelName;
          return copy;
        });
        return this;
      },
      setCity(city: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.city = city;
          return copy;
        });
        return this;
      },
      setSearchTerm(term: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.search_term = term;
          return copy;
        });
        return this;
      },
      setStateCode(stateCode: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.state_code = stateCode;
          return copy;
        });
        return this;
      },
      setCountryCode(countryCode: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.country_code = countryCode;
          return copy;
        });
        return this;
      },
      setProductCategory(productCategory: string) {
        setSearchParams((p) => {
          const copy = { ...p };
          const lc = productCategory.toLowerCase();
          if (lc === 'all') {
            copy.product_category = '';
          } else {
            copy.product_category = productCategory;
          }
          return copy;
        });
        return this;
      },
      setDate(date: Date | 'any' | '') {
        const pad = (n: number) => (n < 10 ? `0${n}` : n);

        setSearchParams((p) => {
          const copy = { ...p };
          if (date === 'any') {
            copy.date = 'any';
            return copy;
          }

          if (date === '') {
            copy.date = '';
            return copy;
          }

          copy.date = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
          return copy;
        });

        return this;
      },
      addProductType(productType: number) {
        setSearchParams((p) => {
          const copy = { ...p };
          if (!copy.filters.product_types.includes(productType)) {
            copy.filters.product_types.push(productType);
          }
          return copy;
        });
        return this;
      },
      removeProductType(productType: number) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.product_types = copy.filters.product_types.filter(
            (pt) => pt !== productType,
          );
          return copy;
        });
        return this;
      },
      updateSearchState(state: SearchState) {
        setSearchParams(state);
        return this;
      },
      clearFilters() {
        setSearchParams((p) => {
          const copy = { ...p };
          const resetFilters = GetDefaultSearchState().filters;
          copy.filters = resetFilters;
          return copy;
        });
        return this;
      },
      async search(debounce = 0) {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(async () => {
          setStatus('searchStart');
          if (customSearcherRef.current && searchParamsRef.current) {
            await customSearcherRef.current(searchParamsRef.current);
          }
          setStatus('searchEnd');
          debounceTimer.current = null;
        }, debounce);
      },
      setAliasId(aliasId: number = 0) {
        setSearchParams((p) => {
          const copy = { ...p };
          copy.filters.city_alias_id = aliasId;
          return copy;
        });
        return this;
      },
    }),
    [searchParams, status],
  );
}

const searchContext = createContext<SearchContextType | undefined>(undefined);

/**
 * Hook to access the search context
 *
 * @returns The search context
 * @throws Error if the hook is used outside of a SearchContextProvider
 */
export function useSearchContext() {
  const context = useContext(searchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchContextProvider');
  }
  return context;
}

/**
 * Provider for the search context
 */
export function DefaultSearchProvider({
  children,
  searchParams,
  customSearch,
}: {
  children: React.ReactNode;
  searchParams?: SearchState;
  customSearch?: (s: SearchState) => Promise<void>;
}) {
  const contextValue = useDefaultContextImplementation(searchParams, customSearch);
  return <searchContext.Provider value={contextValue}>{children}</searchContext.Provider>;
}

/**
 * Provider for the search context
 */
export default searchContext.Provider;
